import { styled } from '@mui/material/styles';
import { UploadManagerIcon } from '../UploadManager/UploadManagerIcon';
import { FormattedMessage } from 'react-intl';
import Button from '../Button/Button';
import IconButton from '../IconButton/IconButton';
import { StyledArrowIcon } from '../UploadManager/UploadManager';
import {
  DownloadInfo,
  DownloadStatus,
} from '@/contexts/DownloadContext';

export const DownloadManagerSummary = ({
  list,
  onDismissAll,
  collapsed,
  onExpand,
}: {
  list: DownloadInfo[];
  onDismissAll: () => void;
  collapsed: boolean;
  onExpand: () => void;
}) => {
  if (list.length === 0) {
    return null;
  }

  const completed = list.filter(
    (download) => download.status === 'completed',
  );
  const failed = list.filter(
    (download) => download.status === 'failed',
  );
  const pending = list.filter(
    (download) => download.status === 'pending',
  );
  const status: DownloadStatus =
    completed.length === list.length
      ? 'completed'
      : failed.length > 0
        ? 'failed'
        : 'pending';

  return (
    <Root status={status}>
      <UploadManagerIcon status={status} />
      <TextWrapper>
        <StatusText
          status={status}
          pendingLength={pending.length}
          failedLength={failed.length}
          totalLength={list.length}
        />
      </TextWrapper>
      <StyledButton
        variant="text"
        onClick={onDismissAll}
        visible={!collapsed && pending.length < list.length}
        aria-label="Dismiss All Button"
      >
        <FormattedMessage
          id="dismissAll"
          defaultMessage="Dismiss All"
        />
      </StyledButton>
      <StyledExpandButton
        visible={collapsed}
        onClick={onExpand}
        aria-label="Expand Button"
      >
        <StyledArrowIcon isOpen={collapsed} />
      </StyledExpandButton>
    </Root>
  );
};

const StatusText = ({
  status,
  pendingLength,
  failedLength,
  totalLength,
}: {
  status: DownloadStatus;
  pendingLength: number;
  failedLength: number;
  totalLength: number;
}) => {
  switch (status) {
    case 'pending':
      return (
        <FormattedMessage
          id="downloadingNoOfItems"
          defaultMessage="Downloading {pendingLength} of {totalLength} items"
          values={{ pendingLength, totalLength }}
        />
      );
    case 'completed':
      return (
        <FormattedMessage
          id="noOfDownloadsStarted"
          defaultMessage="{totalLength, plural, one {# download} other {# downloads}} started"
          values={{ totalLength }}
        />
      );
    case 'failed':
      return (
        <FormattedMessage
          id="itemsFailedToDownload"
          defaultMessage="{failedLength} of {totalLength} downloads failed"
          values={{ failedLength, totalLength }}
        />
      );
  }
};

const Root = styled('div')<{
  status: DownloadStatus;
}>(({ theme, status }) => {
  let backgroundColor = theme.palette.white;

  switch (status) {
    case 'pending':
      backgroundColor = theme.palette.blue500;
      break;
    case 'completed':
      backgroundColor = theme.palette.green500;
      break;
    case 'failed':
      backgroundColor = theme.palette.yellow500;
      break;
  }

  return {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    backgroundColor,
    gap: theme.spacing(2),
    borderRadius: theme.radii.xs,
    transition: 'background 0.2s ease-in-out',
    padding: '14px 16px',
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '20px',

    path: {
      stroke: theme.palette.white,
    },
  };
});

const TextWrapper = styled('div')({
  flexGrow: 1,
});

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'visible',
})<{ visible: boolean }>(({ theme, visible }) => ({
  color: theme.palette.white,
  opacity: visible ? 1 : 0,
  transition: 'all 0.3s ease-in-out',
  transform: visible ? 'translateX(0)' : 'translateX(130px)',
}));

const StyledExpandButton = styled(IconButton)<{ visible: boolean }>(
  ({ theme, visible }) => ({
    position: 'absolute',
    right: visible ? 16 : 60,
    bottom: 14,
    opacity: visible ? 1 : 0,
    cursor: visible ? 'pointer' : 'default',
    pointerEvents: visible ? 'auto' : 'none',

    transition: 'all 0.3s ease-in-out',

    path: {
      stroke: theme.palette.white,
    },
  }),
);
